import React, { useState, useRef, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Eye,
  FileText,
  DollarSign,
  Percent,
  Mail,
  MessageSquare,
  Copy,
  Edit,
  Trash2,
  ShoppingCart,
  Receipt
} from 'lucide-react';
import {
  DropdownContainer,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownIcon
} from './style';
import threeDotsIcon from '../../../../../assets/three-dots-icon.svg';
import { SteelOrder } from '../types';
import { deleteSteelOrder, closeSaleOrder, duplicateSteelOrder, updateSteelOrder } from '../api';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import CloseSaleModal from '../CloseSaleModal/index';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import ModalContext from '../../../../../contexts/ModalContext';

interface ActionsDropdownProps {
  order: SteelOrder;
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({ order }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number; direction: 'up' | 'down' }>({ top: 0, left: 0, width: 0, direction: 'down' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloseSaleModal, setShowCloseSaleModal] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const { setFeedback } = useContext(FeedbackContext);
  const { setModalActive, setModalSection, setSelectedSteelOrder } = useContext(ModalContext);

  const deleteMutation = useMutation({
    mutationFn: (orderId: number) => deleteSteelOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
      setFeedback({ message: `Pedido ${order.id} excluído com sucesso!`, isOpen: true, statusOk: true });
      setShowDeleteModal(false);
      setIsOpen(false);
    },
    onError: (error) => {
      console.error('Erro ao excluir pedido:', error);
      setFeedback({ message: 'Erro ao excluir pedido. Tente novamente.', isOpen: true, statusOk: false });
    },
  });

  const updateSaleStatusMutation = useMutation({
    mutationFn: ({ orderId, status }: { orderId: number; status: boolean | null }) => {
      console.log(`[FRONTEND] Solicitando atualização de status (fechar/cancelar) do pedido ${orderId}...`, status);
      return updateSteelOrder(orderId, { status });
    },
    onSuccess: (data) => {
      console.log(`[FRONTEND] Status atualizado com sucesso. Retorno da API:`, data);
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
      setShowCloseSaleModal(false);
      setIsOpen(false);
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar status da venda:', error);
      const backendMsg = error?.response?.data?.message;
      setFeedback({
        message: backendMsg || 'Erro ao atualizar o status da venda. Tente novamente.',
        isOpen: true,
        statusOk: false,
      });
    },
  });

  const closeSaleMutation = useMutation({
    mutationFn: (orderId: number) => {
      return closeSaleOrder(orderId);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
      setShowCloseSaleModal(false);
      setIsOpen(false);
    },
    onError: (error) => {
      console.error('Erro ao fechar venda no front:', error);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (orderId: number) => duplicateSteelOrder(orderId),
    onSuccess: (duplicatedOrder) => { 
      localStorage.removeItem('reorderedData');
      localStorage.setItem('lastDuplicatedFrom', order.id.toString());
      
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
      setIsOpen(false);
    },
    onError: (error) => {
      console.error('Erro ao duplicar pedido:', error);
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (buttonRef.current && buttonRef.current.contains(target)) ||
        (dropdownRef.current && dropdownRef.current.contains(target))
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScrollOrResize = () => {
      setIsOpen(false);
    };
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  const handleToggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const buttonElement = buttonRef.current;
      const rowElement = buttonElement.closest('tr') as HTMLElement | null;

      const buttonRect = buttonElement.getBoundingClientRect();
      const rowRect = rowElement?.getBoundingClientRect();

      const menuHeight = 400;
      const menuWidth = 200;

      const referenceTop = rowRect ? rowRect.top : buttonRect.top;
      const referenceBottom = rowRect ? rowRect.bottom : buttonRect.bottom;

      const spaceBelow = window.innerHeight - referenceBottom;
      const spaceAbove = referenceTop;

      let direction: 'up' | 'down' = 'down';
      let top = referenceBottom;

      if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
        direction = 'up';
        top = referenceTop - menuHeight;
      } else {
        direction = 'down';
        top = referenceBottom;
      }

      const left =
        buttonRect.left + buttonRect.width / 2 - menuWidth / 2;

      setDropdownPosition({
        top: Math.round(top),
        left: Math.round(left),
        width: menuWidth,
        direction
      });
    }

    setIsOpen(!isOpen);
  };

  const handleAction = (action: string) => {
    setIsOpen(false);

    switch (action) {
      case 'view':
        navigate(`/panel/steel/${order.id}`);
        break;
      case 'edit':
        navigate(`/panel/steel/edit/${order.id}`);
        break;
      case 'close-sale':
        setShowCloseSaleModal(true);
        break;
      case 'generate-pdf': {
        const generatePdfProgrammatically = async () => {
          try {
            setFeedback({ message: 'Gerando PDF, aguarde...', isOpen: true, statusOk: true });
            const { data: fullOrder } = await import('../../../../../config/axios').then(m => m.default.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/order/${order.id}`));
            const { pdf } = await import('@react-pdf/renderer');
            const { default: OrderPDFDocument } = await import('../../components/OrderPDFDocument');
            const blob = await pdf(<OrderPDFDocument orderData={fullOrder} />).toBlob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
          } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            setFeedback({ message: 'Erro ao gerar o PDF. Tente novamente.', isOpen: true, statusOk: false });
          }
        };
        generatePdfProgrammatically();
        break;
      }
      case 'generate-nf':
        setSelectedSteelOrder(order);
        setModalSection('generateOrderInvoice');
        setModalActive(true);
        break;
      case 'invoice':
        break;
      case 'commission':
        break;
      case 'send-email':
        break;
      case 'send-whatsapp':
        break;
      case 'duplicate':
        duplicateMutation.mutate(order.id);
        break;
      case 'delete':
        setShowDeleteModal(true);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = () => {
    deleteMutation.mutate(order.id);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmCloseSale = () => {
    updateSaleStatusMutation.mutate({ orderId: order.id, status: true });
  };

  const handleCancelSale = () => {
    updateSaleStatusMutation.mutate({ orderId: order.id, status: false });
  };

  const handleCancelCloseSale = () => {
    setShowCloseSaleModal(false);
  };

  return (
    <>
      <DropdownContainer>
        <DropdownButton ref={buttonRef} onClick={handleToggleDropdown}>
          <DropdownIcon src={threeDotsIcon} alt="Opções" />
        </DropdownButton>
      </DropdownContainer>
      {isOpen && ReactDOM.createPortal(
        <DropdownMenu
          ref={dropdownRef}
          top={dropdownPosition.top}
          left={dropdownPosition.left}
          width={dropdownPosition.width}
          direction={dropdownPosition.direction}
        >
          {(order.status === null || order.status === undefined) && (
            <DropdownItem onClick={() => handleAction('close-sale')}>
              <ShoppingCart size={16} />
              Fechar venda
            </DropdownItem>
          )}
          {(order.status === true || order.status === false) && (
            <DropdownItem onClick={() => handleAction('revert-sale')}>
              <ShoppingCart size={16} />
              Reverter venda
            </DropdownItem>
          )}
          <DropdownItem onClick={() => handleAction('generate-pdf')}>
            <FileText size={16} />
            Gerar PDF
          </DropdownItem>
          {order.status === true && (
            <DropdownItem onClick={() => handleAction('generate-nf')}>
              <Receipt size={16} />
              Gerar NF
            </DropdownItem>
          )}
          <DropdownItem onClick={() => handleAction('invoice')}>
            <DollarSign size={16} />
            Faturar
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('commission')}>
            <Percent size={16} />
            Comissão
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('send-email')}>
            <Mail size={16} />
            Enviar por e-mail
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('send-whatsapp')}>
            <MessageSquare size={16} />
            Enviar por Whatsapp
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('view')}>
            <Eye size={16} />
            Visualizar
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('duplicate')}>
            <Copy size={16} />
            Duplicar
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('edit')}>
            <Edit size={16} />
            Editar
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('delete')}>
            <Trash2 size={16} />
            Excluir
          </DropdownItem>
        </DropdownMenu>,
        document.body
      )}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Pedido"
        message={`Tem certeza que deseja excluir o pedido ${order.id}? Esta ação não pode ser desfeita.`}
        actions={[
          {
            label: 'Cancelar',
            onClick: handleCancelDelete,
            variant: 'outline'
          },
          {
            label: deleteMutation.isPending ? 'Excluindo...' : 'Excluir',
            onClick: handleConfirmDelete,
            variant: 'danger'
          }
        ]}
      />
      <CloseSaleModal
        isOpen={showCloseSaleModal}
        onClose={handleCancelCloseSale}
        onConfirm={handleConfirmCloseSale}
        onCancelSale={handleCancelSale}
        orderId={order.id}
        isLoading={updateSaleStatusMutation.isPending}
      />
    </>
  );
};

export default ActionsDropdown;
